import React from 'react'
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import {
  CATEGORIES,
  DEFAULT_QUICK_DRUGS,
  LOGBOOK_PROCEDURES,
} from '../../patientRecords/ePCR/liveCapture/catalogue'

const categoryLabel = (key) =>
  CATEGORIES.find((category) => category.key === key)?.label ?? key

// Site Configuration -> Patient Records -> Critical care. Controlled
// component: `value` is orgSettings.criticalCareSettings, saved by the parent
// form as a top-level `criticalCareSettings` key on PUT /site-config.
const CriticalCareSettings = ({ value, onChange }) => {
  const usingDefaults = !value?.quickDrugs?.length
  const quickDrugs = value?.quickDrugs ?? []
  const skillCurrency = value?.skillCurrency ?? []

  const update = (patch) => onChange((prev) => ({ ...(prev ?? {}), ...patch }))

  const updateDrug = (index, patch) =>
    update({
      quickDrugs: quickDrugs.map((drug, i) =>
        i === index ? { ...drug, ...patch } : drug,
      ),
    })

  const updateTarget = (index, patch) =>
    update({
      skillCurrency: skillCurrency.map((target, i) =>
        i === index ? { ...target, ...patch } : target,
      ),
    })

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        <Typography variant="title3">Critical care</Typography>
        <Typography variant="caption">
          Settings for the Critical Care record type and its live consoles.
          Quick drugs are a documentation aid - doses shown on the buttons are
          what your organisation sets here, and crews confirm what was given.
        </Typography>
      </Stack>

      <Stack gap={1}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={1}
        >
          <Typography fontWeight={700}>Quick drugs</Typography>
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={() =>
                update({
                  quickDrugs: DEFAULT_QUICK_DRUGS.map((drug) => ({
                    ...drug,
                    categories: [...(drug.categories ?? [])],
                  })),
                })
              }
            >
              Load defaults
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() =>
                update({
                  quickDrugs: [
                    ...quickDrugs,
                    { name: '', dose: '', route: '', categories: [] },
                  ],
                })
              }
            >
              Add drug
            </Button>
          </Stack>
        </Stack>
        {usingDefaults && (
          <Alert severity="info">
            No list set - consoles use the AmbuNet default list (
            {DEFAULT_QUICK_DRUGS.length} drugs). Load the defaults to edit them.
          </Alert>
        )}
        {quickDrugs.map((drug, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 1.5 }}>
            <Stack gap={1}>
              <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                <TextField
                  size="small"
                  label="Medicine"
                  value={drug.name ?? ''}
                  onChange={(e) => updateDrug(index, { name: e.target.value })}
                  sx={{ flex: 2 }}
                />
                <TextField
                  size="small"
                  label="Dose"
                  placeholder="e.g. 1mg (optional)"
                  value={drug.dose ?? ''}
                  onChange={(e) => updateDrug(index, { dose: e.target.value })}
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Route"
                  value={drug.route ?? ''}
                  onChange={(e) => updateDrug(index, { route: e.target.value })}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  color="error"
                  onClick={() =>
                    update({
                      quickDrugs: quickDrugs.filter((x, i) => i !== index),
                    })
                  }
                  sx={{ alignSelf: { xs: 'flex-end', md: 'center' } }}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
              <Autocomplete
                multiple
                size="small"
                options={CATEGORIES.map((category) => category.key)}
                getOptionLabel={categoryLabel}
                value={drug.categories ?? []}
                onChange={(e, categories) => updateDrug(index, { categories })}
                renderTags={(tags, getTagProps) =>
                  tags.map((tag, tagIndex) => {
                    const { key, ...chipProps } = getTagProps({
                      index: tagIndex,
                    })
                    return (
                      <Chip
                        key={key}
                        size="small"
                        label={categoryLabel(tag)}
                        sx={{ borderRadius: 1 }}
                        {...chipProps}
                      />
                    )
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Show on"
                    helperText="Leave empty to show on every console"
                  />
                )}
              />
            </Stack>
          </Paper>
        ))}
      </Stack>

      <Stack gap={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
        >
          <Stack>
            <Typography fontWeight={700}>Skill currency targets</Typography>
            <Typography variant="caption" color="text.secondary">
              Shown on each clinician's procedure logbook, e.g. 6 intubations
              every 90 days.
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() =>
              update({
                skillCurrency: [
                  ...skillCurrency,
                  { procedure: '', count: 1, periodDays: 90 },
                ],
              })
            }
          >
            Add target
          </Button>
        </Stack>
        {skillCurrency.map((target, index) => (
          <Stack
            key={index}
            direction={{ xs: 'column', sm: 'row' }}
            gap={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <FormControl size="small" sx={{ flex: 2 }}>
              <InputLabel>Procedure</InputLabel>
              <Select
                label="Procedure"
                value={target.procedure ?? ''}
                onChange={(e) =>
                  updateTarget(index, { procedure: e.target.value })
                }
              >
                {LOGBOOK_PROCEDURES.map((procedure) => (
                  <MenuItem key={procedure.key} value={procedure.key}>
                    {procedure.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label="At least"
              value={target.count ?? ''}
              inputProps={{ min: 0 }}
              onChange={(e) => updateTarget(index, { count: e.target.value })}
              sx={{ flex: 1 }}
            />
            <TextField
              size="small"
              type="number"
              label="Every"
              value={target.periodDays ?? ''}
              inputProps={{ min: 1 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">days</InputAdornment>
                ),
              }}
              onChange={(e) =>
                updateTarget(index, { periodDays: e.target.value })
              }
              sx={{ flex: 1 }}
            />
            <IconButton
              color="error"
              onClick={() =>
                update({
                  skillCurrency: skillCurrency.filter((x, i) => i !== index),
                })
              }
              sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
            >
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>

      <FormControlLabel
        label={
          <Stack>
            <Typography fontWeight={600}>Automatic case review</Typography>
            <Typography variant="caption" color="text.secondary">
              Flag critical care records for clinical case review when they are
              submitted with a failed or surgical airway, multiple intubation
              attempts, peri-intubation hypoxia or hypotension, thoracotomy,
              REBOA, hysterotomy or a sedation adverse event.
            </Typography>
          </Stack>
        }
        sx={{ alignItems: 'flex-start' }}
        control={
          <Switch
            checked={value?.autoCaseReview ?? true}
            onChange={(e) => update({ autoCaseReview: e.target.checked })}
            color="success"
          />
        }
      />
    </Stack>
  )
}

export default CriticalCareSettings
